import React, { useMemo, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Flex, Box, Center, Spinner } from '@chakra-ui/react';

import { renderHeader, renderCell } from '../components/table';
import { Wrapper } from '../components/Wrapper';
import { BreakdownBy } from '../components/breakdown-by';
import {
  EmptyDataWrapper,
  isReportTableEmpty,
} from '../components/empty-data-wrapper';
import { TitleWithTooltip } from '../components/title-with-tooltip';

import { ReportSelectionContext } from '@/contextProviders/ReportSelection';
import {
  selectOfferTable,
  selectOfferReportsGroup,
  selectOfferChartLoading,
} from '@/store/atsReports/offers/selectors';

import { applyGroup } from '@/store/atsReports/offers';

import { secondsToHumanTime } from '@/pages/reports/non-ats-reports/ats/table';
import { rateOfChange } from '../helpers/rate-of-change';
import { useKFlags } from '@/hooks/use-k-flags';
import { useDrillDownHandler } from '../drilldown/use-drilldownHeader';
import { Table } from '@/components/ats';
import { KText } from '@/ui-library/components';
import { EmptyReportChart } from '@/pages/reports/custom-reports/components/ReportCharts/EmptyReportChart';

interface Option {
  label: string;
  id: number | string;
}

const GROUP_BY_OPTIONS = [
  { id: null, label: 'Time', key: 'dt' },
  { id: 'job_name', label: 'Job name', key: 'g1.name' },
  { id: 'primary_recruiter', label: 'Primary recruiter', key: 'g1.name' },
  { id: 'primary_hm', label: 'Primary hiring manager', key: 'g1.name' },
  {
    id: 'appl_contact_source',
    label: 'Source (on application)',
    key: 'g1?.name',
  },
  { id: 'department', label: 'Department', key: 'g1.name' },
  { id: 'appl_location', label: 'Location (on job)', key: 'g1.name' },
];

const BREAKDOWN_OPTIONS = [
  { id: null, label: 'None' },
  ...GROUP_BY_OPTIONS.filter((option) => option.id !== null),
];

export const TableView: React.FC<{
  isFullScreen?: boolean;
  isFilterApplied?: boolean;
  groupBy1?: Option;
  groupBy2?: Option;
  customBreakdownChange?: (key: string, option: Option) => void;
  customData?: any[];
  isReadOnly?: boolean;
  isPrintView?: boolean;
  renderTitle?: () => React.ReactNode;
  onDrillDownOpen?: (data: any) => void;
  isStaticReport?: boolean;
  onSortChange?: (sortState: string) => void;
  isEditMode?: boolean;
  tableColStyles?: any;
  onTableColDataChange?: (tableColData: {
    order: string[];
    visibility: Record<string, boolean>;
  }) => void;
  initialTableColData?: {
    order: string[];
    visibility: Record<string, boolean>;
  };
  skipLocalStorage?: boolean;
  feReportsV3?: boolean;
}> = ({
  isFullScreen = false,
  isFilterApplied = false,
  groupBy1,
  groupBy2,
  isPrintView = false,
  customBreakdownChange,
  customData,
  isReadOnly = false,
  renderTitle,
  onDrillDownOpen = () => {},
  isStaticReport = false,
  onSortChange = (sortState) => {},
  isEditMode = false,
  tableColStyles = {},
  onTableColDataChange = undefined,
  initialTableColData = undefined,
  skipLocalStorage = false,
  feReportsV3 = false,
}) => {
  const dispatch = useDispatch();
  const { consumer } = useContext(ReportSelectionContext);
  const { feDrilldown } = useKFlags();
  const reduxIsLoading = useSelector(selectOfferChartLoading);
  const reduxOfferTable = useSelector(selectOfferTable);
  const reduxGroup = useSelector(selectOfferReportsGroup);

  const isTableLoading = feReportsV3 ? false : reduxIsLoading;
  const offerTable = feReportsV3 ? [] : reduxOfferTable;
  const group = feReportsV3 ? null : reduxGroup;

  const dataToUse = customData || offerTable;
  const isDataEmpty = isReportTableEmpty(dataToUse, 'offers');
  const { handleDrillDown } = useDrillDownHandler(feDrilldown, onDrillDownOpen);
  const getGroupValue = (value: any, fallbackValue: any) =>
    feReportsV3 ? value : value || fallbackValue;

  const drilldownGroup = {
    group_by_1: getGroupValue(groupBy1, group?.group_by_1),
    group_by_2: getGroupValue(groupBy2, group?.group_by_2),
  };

  const columns = useMemo(() => {
    const header = groupBy1 || group?.group_by_1 || GROUP_BY_OPTIONS[0];

    return [
      {
        ...renderHeader({
          accessor: 'header',
          title: header?.label || 'Name',
          width: 300,
          expand: false,
          sort: true,
          isReadOnly,
          groupBy: group?.group_by_1?.id,
          breakDownBy: group?.group_by_2?.id,
          renderFooter: () => <KText size="sm-semi-bold">Total</KText>,
        }),
        id: 'header',
        title: header?.label || 'Name',
        columnConfig: false,
      },

      {
        ...renderCell({
          title: 'Offers',
          accessor: 'offers',
          width: 100,
          sort: true,
          render: (_, row) => (
            <Flex
              _hover={{ textDecoration: feDrilldown ? 'underline' : 'none' }}
              cursor={feDrilldown ? 'pointer' : 'default'}
              onClick={() =>
                handleDrillDown(
                  row,
                  'Offers',
                  false,
                  drilldownGroup,
                  null,
                  row?.offers
                )
              }
            >
              {row?.offers}
            </Flex>
          ),
          renderFooter: (info) => {
            const row = info?.rows?.[0]?.original;
            return (
              <Flex
                _hover={{ textDecoration: feDrilldown ? 'underline' : 'none' }}
                cursor={feDrilldown ? 'pointer' : 'default'}
                onClick={() =>
                  handleDrillDown(
                    row,
                    'Offers',
                    true,
                    drilldownGroup,
                    null,
                    row?.offers_total
                  )
                }
              >
                <KText size="sm-semi-bold">{row?.offers_total}</KText>
              </Flex>
            );
          },
          id: 'offers',
          columnConfig: true,
        }),
      },
      {
        ...renderCell({
          title: 'Offer acceptance rate',
          accessor: 'offers_acceptance',
          width: 150,
          sort: true,
          render: (_, row) => (
            <Flex
              _hover={{ textDecoration: 'underline' }}
              cursor="pointer"
              onClick={() =>
                handleDrillDown(
                  row,
                  'Offer acceptance rate',
                  false,
                  drilldownGroup,
                  null,
                  `${rateOfChange({
                    numerator: row?.accepted_offers,
                    denominator: row?.offers,
                  })}
            %`
                )
              }
            >
              {rateOfChange({
                numerator: row?.accepted_offers,
                denominator: row?.offers,
              })}
              %
            </Flex>
          ),
          id: 'offers_acceptance',
          columnConfig: true,
        }),
        sortType: (rowA, rowB) => {
          const acceptedOffersA = parseInt(rowA.original?.accepted_offers) || 0;
          const offersA = parseInt(rowA.original?.offers) || 0;
          const acceptedOffersB = parseInt(rowB.original?.accepted_offers) || 0;
          const offersB = parseInt(rowB.original?.offers) || 0;

          const rateA = offersA > 0 ? acceptedOffersA / offersA : 0;
          const rateB = offersB > 0 ? acceptedOffersB / offersB : 0;

          return rateA - rateB;
        },
      },

      {
        ...renderCell({
          id: 'time_to_offer',
          title: 'Avg. time to offer',
          columnConfig: true,
          accessor: 'time_to_offer',
          width: 100,
          sort: true,
          render: (_, row) => (
            <Flex
              _hover={{ textDecoration: 'underline' }}
              cursor="pointer"
              onClick={() =>
                handleDrillDown(
                  row,
                  'Time to offer',
                  false,
                  drilldownGroup,
                  null,
                  secondsToHumanTime(row?.time_to_offer / row?.offers)
                )
              }
            >
              {secondsToHumanTime(row?.time_to_offer / row?.offers)}
            </Flex>
          ),
        }),
        sortType: (rowA, rowB) => {
          const offersA = parseInt(rowA.original?.offers) || 0;
          const offersB = parseInt(rowB.original?.offers) || 0;
          const avgA =
            offersA > 0
              ? parseInt(rowA.values?.time_to_offer || 0) / offersA
              : 0;
          const avgB =
            offersB > 0
              ? parseInt(rowB.values?.time_to_offer || 0) / offersB
              : 0;
          return avgA - avgB;
        },
      },

      {
        ...renderCell({
          id: 'time_to_close_offer',
          columnConfig: true,
          title: 'Avg. time to close offer',
          accessor: 'time_to_close_offer',
          width: 100,
          sort: true,
          render: (_, row) => {
            const avgTimeToCloseOffer =
              parseInt(row?.time_to_close_offer) / parseInt(row?.offers);
            return (
              <Flex
                _hover={{ textDecoration: 'underline' }}
                cursor="pointer"
                onClick={() =>
                  handleDrillDown(
                    row,
                    'Time to close offer',
                    false,
                    drilldownGroup,
                    null,
                    secondsToHumanTime(avgTimeToCloseOffer)
                  )
                }
              >
                {secondsToHumanTime(avgTimeToCloseOffer)}
              </Flex>
            );
          },
        }),
        sortType: (rowA, rowB) => {
          const offersA = parseInt(rowA.original?.offers) || 0;
          const offersB = parseInt(rowB.original?.offers) || 0;
          const avgA =
            offersA > 0
              ? parseInt(rowA.values?.time_to_close_offer || 0) / offersA
              : 0;
          const avgB =
            offersB > 0
              ? parseInt(rowB.values?.time_to_close_offer || 0) / offersB
              : 0;
          return avgA - avgB;
        },
      },
    ];
  }, [groupBy1, groupBy2, feReportsV3, group]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBreakdownChange = (key: string, option: Option) => {
    const actionMap: Record<string, string> = {
      group_by_1: 'group_by_1',
      group_by_2: 'group_by_2',
    };

    if (actionMap[key]) {
      dispatch(applyGroup({ key: actionMap[key], value: option }));
    }
  };

  const handleSortChange = (state) => {
    const { sortBy } = state;
    const currentSort = sortBy?.[0];
    onSortChange(currentSort);
  };

  const breakdownConfig = [
    {
      key: 'group_by_1',
      prefix: 'Group by',
      options: GROUP_BY_OPTIONS,
      selected: feReportsV3
        ? groupBy1 || GROUP_BY_OPTIONS[0]
        : groupBy1 || group?.group_by_1 || GROUP_BY_OPTIONS[0],
    },
    {
      key: 'group_by_2',
      prefix: 'Breakdown by',
      options: BREAKDOWN_OPTIONS,
      selected: feReportsV3
        ? groupBy2 || BREAKDOWN_OPTIONS[0]
        : groupBy2 || group?.group_by_2 || BREAKDOWN_OPTIONS[0],
    },
  ];

  if (isFilterApplied) {
    return <EmptyReportChart text="Filters not applicable" subText="The applied filters are not applicable for this block" />;
  }

  return (
    <Wrapper role="group" isReadOnly={isReadOnly || isStaticReport}>
      {consumer === 'DEFAULT' && (
        <Flex
          alignItems="flex-start"
          justifyContent="space-between"
          position="sticky"
          top="0"
          zIndex="6"
          bg="white"
        >
          {renderTitle ? (
            renderTitle()
          ) : (
            <Box flex="1">
              <TitleWithTooltip
                label="Offer breakdown"
                metricDefinition="Comprehensive breakdown of the offer data"
              />
            </Box>
          )}
          <Flex
            gap="8px"
            mr={
              isPrintView
                ? '0'
                : isDataEmpty
                ? '35px'
                : isEditMode
                ? '55px'
                : '35px'
            }
            mb="12px"
          >
            {breakdownConfig.map(({ key, prefix, options, selected }) => (
              <BreakdownBy
                key={key}
                isPortal={!isFullScreen}
                prefix={prefix}
                options={options}
                selected={selected}
                isEditMode={isEditMode}
                onChange={(option: Option) =>
                  customBreakdownChange
                    ? customBreakdownChange(key, option)
                    : handleBreakdownChange(key, option)
                }
              />
            ))}
          </Flex>
        </Flex>
      )}
      {isTableLoading ? (
        <Center minHeight="200px">
          <Spinner color="primary-800" size="sm" />
        </Center>
      ) : (
        <EmptyDataWrapper data={dataToUse} field="offers">
          <Table
            tableId="offer-table"
            skipLocalStorage={skipLocalStorage}
            defaultSortBy={[{ id: 'header', desc: false }]}
            isColumnLevelSorting
            enableColumnConfig={isEditMode}
            tableColStyles={tableColStyles}
            columns={columns}
            data={dataToUse}
            enableFooter={true}
            layoutScrollContainerStyles="c-data-table wide-content table-container"
            enableExpanded={true}
            layoutStyles={{
              borderBottom: 'none',
            }}
            getTableState={handleSortChange}
            onTableColDataChange={onTableColDataChange}
            initialTableColData={initialTableColData}
          />
        </EmptyDataWrapper>
      )}
    </Wrapper>
  );
};
